if (!customElements.get('custom-discount-popup')) {
  customElements.define(
    'custom-discount-popup',
    class CustomDiscountPopup extends HTMLElement {
      constructor() {
        super();
        this.cookieName = 'shopify:custom-discount-popup';
        this.delaySeconds = parseInt(this.dataset.delaySeconds) || 5;
        this.discountCode = this.dataset.discountCode;
        this.triggerOpen = this.dataset.triggerOpen;
        this.repeatOpen = this.dataset.repeatOpen;
      }
      
      connectedCallback() {
        // Track original location
        this._originalParent = document.body;
        
        // Use MutationObserver to efficiently monitor parent changes
        if (window.MutationObserver) {
          this._positionObserver = new MutationObserver(() => {
            if (this.parentElement !== this._originalParent && this.parentElement !== null) {
              console.warn('[CustomDiscountPopup] was moved unexpectedly. Restoring to body.');
              document.body.appendChild(this);
            }
          });
          
          // Observe the document body for changes to its direct children
          // This will detect when our element is moved to a different parent
          this._positionObserver.observe(document.body, { 
            childList: true,     // Monitor additions/removals of child nodes
            subtree: false,      // Don't monitor descendants
            attributes: false    // Don't monitor attribute changes
          });
        }
        
        // Find elements in the DOM now that it's connected
        this.closeButton = this.querySelector('.custom-discount-popup__close');
        this.overlay = this.querySelector('.drawer__overlay');
        this.ctaButton = this.querySelector('.custom-discount-popup__cta-button');
        
        // Preload the popup image to prevent layout shifts when shown
        this.preloadPopupImage();
        
        // Use event delegation for close button clicks - more efficient and robust
        const modalContent = this.querySelector('.modal-content');
        if (modalContent) {
          modalContent.addEventListener('click', (event) => {
            // Only close if clicking the close button or its children
            if (event.target.closest('.custom-discount-popup__close')) {
              event.preventDefault();
              event.stopPropagation();
              this.hide();
            }
          });
        }
        
        if (this.overlay) {
          this.overlay.addEventListener('click', this.hide.bind(this));
        }
        
        if (this.ctaButton) {
          this.ctaButton.addEventListener('click', this.applyDiscount.bind(this));
        }
        
        // Handle escape key
        document.addEventListener('keyup', (event) => {
          if (event.code === 'Escape' && this.isVisible) {
            this.hide();
          }
        });
        
        // Check if should show popup
        this.handleOpen();
      }
      
      /**
       * Preload popup image to prevent layout shifts
       */
      preloadPopupImage() {
        const imageElement = this.querySelector('.custom-discount-popup__image');
        if (imageElement && imageElement.src) {
          // Create a new image object to preload the image
          const imagePreloader = new Image();
          imagePreloader.src = imageElement.src;
          
          // Store the preloader to prevent garbage collection before load completes
          this._imagePreloader = imagePreloader;
          
          // Log success in development mode
          imagePreloader.onload = () => {
            if (window.Shopify && window.Shopify.designMode) {
              console.log('[CustomDiscountPopup] Image preloaded successfully');
            }
            // Free the reference after load
            this._imagePreloader = null;
          };
        }
      }
      
      /**
       * Handle opening the popup based on trigger settings
       */
      async handleOpen() {
        if (Shopify.designMode) return;
        
        // Check cookie to see if popup should be shown
        let savedData = this.getStorage();
        if (savedData && savedData.opened) {
          return false;
        }
        
        // Check if hide_if_discount is enabled and cart has order-level discount
        if (this.dataset.hideIfDiscount === 'true') {
          const hasOrderLevelDiscount = await this.hasCartDiscount();
          if (hasOrderLevelDiscount) return false;
        }
        
        // Handle different trigger types
        switch (this.triggerOpen) {
          case 'delay':
            setTimeout(() => {
              this.show();
            }, this.delaySeconds * 1000);
            break;
            
          case 'scroll_down':
            this.triggerAfterScroll(700, this.show.bind(this));
            break;
            
          case 'immediately':
          default:
            this.show();
            break;
        }
      }
      
      /**
       * Trigger popup after scrolling down
       */
      triggerAfterScroll(offset = 500, trigger) {
        let opened = false;
        const start = 0;
        
        window.addEventListener('scroll', () => {
          let scrollTop = window.pageYOffset || 
                        (document.documentElement || document.body.parentNode || document.body).scrollTop;
          
          if (scrollTop - start > offset && !opened) {
            trigger();
            opened = true;
          }
        });
      }
      
      /**
       * Show the popup
       */
      show() {
        if (this.isVisible) return;
        
        // Show the popup
        // Step 1: Explicitly remove the 'hidden' HTML attribute which may prevent visibility
        // This is critical as the 'hidden' attribute can override CSS display properties
        if (this.hasAttribute('hidden')) {
          this.removeAttribute('hidden');
        }
        
        // Step 2: Set the HTMLElement.hidden property to false for backward compatibility
        // Note: This alone is not sufficient as it doesn't remove the attribute in all browsers
        this.hidden = false;
        
        // Step 3: Add the 'active' class and 'open' attribute which controls CSS visibility
        this.classList.add('active');
        this.setAttribute('open', '');
        
        // Step 4: Force reflow, then trigger animation with staging delay (matches theme)
        this.offsetHeight; // Force reflow
        
        // Step 5: Add 'active' attribute after 75ms staging delay to match theme behavior
        setTimeout(() => {
          this.setAttribute('active', '');
        }, 75);
        
        // Step 6: Skip scroll lock for this popup to maintain page scrollability
        // document.body.classList.add('modal-open');
        
        // Step 5: Add focus trapping for accessibility if FoxTheme utility exists
        if (window.FoxTheme && FoxTheme.a11y && FoxTheme.a11y.trapFocus) {
          FoxTheme.a11y.trapFocus(this, this.ctaButton || this.querySelector('button'));
        }
        
        // Set visibility flag for internal state tracking
        this.isVisible = true;
        
        // Set storage for repeat behavior management
        this.setRepeatOpen();
      }
      
      /**
       * Hide the popup
       */
      hide() {
        // Only log in development mode
        if (window.Shopify && window.Shopify.designMode) {
          console.log('Hide method called');
        }
        
        // Hide the popup
        this.classList.remove('active');
        this.removeAttribute('open');
        this.removeAttribute('active');
        // document.body.classList.remove('modal-open'); // Skip - no scroll lock was added
        
        // Remove focus trapping if FoxTheme utility exists
        if (window.FoxTheme && FoxTheme.a11y && FoxTheme.a11y.removeTrapFocus) {
          FoxTheme.a11y.removeTrapFocus();
        }
        
        this.isVisible = false;
        
        // Clean up immediately 
        this.hidden = true;
        
        // Only log in development mode
        if (window.Shopify && window.Shopify.designMode) {
          console.log('Popup hidden');
        }
        
        // Set flag
        this.isVisible = false;
      }
      
      /**
       * Apply discount code by redirecting to Shopify's discount URL with quiz page redirect
       * This provides immediate feedback and leverages Shopify's native discount handling
       */
      applyDiscount(event) {
        event.preventDefault();
        
        const discountCode = this.discountCode;
        if (!discountCode) return;
        
        // Hide the popup immediately for better user experience
        this.hide();
        
        // Redirect to Shopify's discount URL with quiz page as the redirect destination
        window.location.href = `/discount/${discountCode}?redirect=/pages/quiz`;
      }
      
      /**
       * Set storage for repeat open frequency
       */
      setRepeatOpen() {
        if (Shopify.designMode) return;
        
        let expires;
        switch (this.repeatOpen) {
          case 'no_repeat':
            expires = 365;
            break;
          case 'every_day':
            expires = 1;
            break;
          case 'every_3_days':
            expires = 3;
            break;
          case 'every_week':
            expires = 7;
            break;
          default:
            expires = 1;
            break;
        }
        
        this.setStorage({ opened: true }, expires);
      }
      
      /**
       * Get storage data
       */
      getStorage() {
        const item = localStorage.getItem(this.cookieName);
        if (!item) return null;
        
        try {
          const data = JSON.parse(item);
          const expiresAt = new Date(data.expiresAt);
          if (expiresAt < new Date()) {
            localStorage.removeItem(this.cookieName);
            return null;
          }
          return data.value;
        } catch (e) {
          return null;
        }
      }
      
      /**
       * Set storage data
       */
      setStorage(value, days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        
        const data = {
          value: value,
          expiresAt: date.toISOString()
        };
        
        localStorage.setItem(this.cookieName, JSON.stringify(data));
      }
      
      /**
       * Check if cart has order-level discounts applied
       * @returns {Promise<boolean>} True if order-level discounts are applied
       */
      async hasCartDiscount() {
        try {
          if (this._cartCheckOnce) return this._cartCheckOnce;
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3000);
          const p = (async () => {
            const res = await fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
            clearTimeout(timeoutId);
            if (!res.ok) return false;
            const cart = await res.json();
            if (cart.discount_codes && cart.discount_codes.length > 0) return true;
            return Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length > 0;
          })().catch(() => false);
          this._cartCheckOnce = p;
          return await p;
        } catch (_) {
          return false; // fail-open: allow popup
        }
      }
      
      /**
       * Clean up when element is removed
       */
      disconnectedCallback() {
        // Clean up position observer
        if (this._positionObserver) {
          this._positionObserver.disconnect();
        }
      }
    }
  );
}
